import { NOTE_CLASS } from "../../lib/ui.js";

// Text roles.
//
// Two questions decide the role of a string: whose statement is it - this
// dashboard's framing of the controls, or what an agent will do on an
// endpoint - and does it move when the administrator changes a control. Ours
// and static stays quiet. Endpoint behaviour that moves with the selection
// carries the contrast, because that is what an administrator opens the page to
// read. A string that never changes with the selection is never an OnEndpoints.
//
// Place input syntax and worked examples beside the input. Explain absent,
// empty, and exclusive-list behavior in the selected outcome. Add workflow
// impact and a specific pilot check only when the selection creates a relevant
// risk. Expandable details are for matching semantics, not repeated key names
// or a fixed questionnaire. Simple controls need only a label and outcome.
const ROLE_TAG_CLASS = "text-[9px] uppercase leading-[1.1] tracking-[0.1em]";
const ROLE_BODY_CLASS = "m-0 text-[11px] leading-[1.35] text-[color:var(--ink)]";
const ROLE_RULE_CLASS = "grid gap-[4px] border-l-2 py-[2px] pl-[11px]";

/// Role 1. Explains a grouping this dashboard invented for presentation. Once
/// per group, at its header, never repeated per control.
export function Orientation({ children }) {
    return <p className={`${NOTE_CLASS} m-0`}>{children}</p>;
}

/// Role 2. Describes the input itself. Leave it out wherever an OnEndpoints
/// line already says the same thing one step better.
export function Mechanics({ children }) {
    return <p className="m-0 text-[10px] leading-[1.3] text-[color:var(--muted-soft)]">{children}</p>;
}

/// Role 3. What a managed session does under the current selection. `written`
/// means the selection puts a key in the document; an unwritten state keeps the
/// neutral rule, because an absent value adds no restriction from this policy.
export function OnEndpoints({ written = false, children }) {
    return (
        <div className={`${ROLE_RULE_CLASS} ${written ? "border-[color:var(--green)]" : "border-[color:var(--line-strong)]"}`}>
            <span className={`${ROLE_TAG_CLASS} ${written ? "text-[color:var(--green)]" : "text-[color:var(--eyebrow)]"}`}>With this draft</span>
            <p className={ROLE_BODY_CLASS}>{children}</p>
        </div>
    );
}

/// Role 4. Behaviour that depends on another control, or on the agent doing
/// something this page cannot decide. `scope` names what it depends on.
export function Interaction({ scope, children }) {
    return (
        <div className={`${ROLE_RULE_CLASS} border-[#aa742f]`}>
            <span className={`${ROLE_TAG_CLASS} text-[color:var(--muted)]`}>Interacts with <span className="tracking-[0.06em] text-[color:var(--muted-soft)]">{scope}</span></span>
            <p className={ROLE_BODY_CLASS}>{children}</p>
        </div>
    );
}

/// Role 5. The only filled block among the roles, so a fill always means the
/// save is stopped until this is resolved.
export function Blocker({ children }) {
    return (
        <div className="grid gap-[4px] border-l-2 border-[#aa742f] bg-[#aa742f]/10 py-[8px] pr-[10px] pl-[11px]">
            <span className={`${ROLE_TAG_CLASS} text-[color:var(--muted)]`}>Blocks pilot</span>
            <p className={ROLE_BODY_CLASS}>{children}</p>
        </div>
    );
}

/// Role 6. The configuration keys a group writes, as reference rather
/// than prose.
export function SettingKeys({ keys }) {
    return (
        <div className="flex flex-wrap justify-end gap-[4px]">
            {keys.map((key) => <code key={key} className="border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] py-[2px] text-[9px] leading-[1.3] text-[color:var(--muted-soft)]">{key}</code>)}
        </div>
    );
}

export function GroupHeader({ title, orientation, keys }) {
    return (
        <header className="grid gap-[3px]">
            <div className="flex flex-wrap items-baseline justify-between gap-x-[12px] gap-y-[5px]">
                <h3 className="m-0 text-[12px] font-medium text-[color:var(--ink)]">{title}</h3>
                {keys?.length ? <SettingKeys keys={keys} /> : null}
            </div>
            {orientation ? <Orientation>{orientation}</Orientation> : null}
        </header>
    );
}

export function WorkflowImpact({ children, test }) {
    return <div className="grid gap-[6px]">
        {children ? <div className={`${ROLE_RULE_CLASS} border-[#aa742f]`}>
            <span className={`${ROLE_TAG_CLASS} text-[color:var(--muted)]`}>Workflow impact</span>
            <p className={ROLE_BODY_CLASS}>{children}</p>
        </div> : null}
        {test ? <div className={`${ROLE_RULE_CLASS} border-[color:var(--line-strong)]`}>
            <span className={`${ROLE_TAG_CLASS} text-[color:var(--eyebrow)]`}>Test in pilot</span>
            <p className={ROLE_BODY_CLASS}>{test}</p>
        </div> : null}
    </div>;
}
